/**
 * The example ranking's strip (user, 15/10/2026: "fake texts with fake raw
 * score — even when it's empty you understand the formula"): one line that
 * says these texts are made up and scored on your formula, the details
 * behind ⓘ, and — when the campaign has texts of its own, none ranked yet —
 * the switch between them and the example.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { FlaskConical } from 'lucide-react';

import { HintDot, SegmentedPills } from '@/components/ui';

import type { ResultsSource } from './example-view';

const HINT =
  'No text of this campaign has results yet, so the panel shows 8 made-up texts in 3 made-up prompt groups, with made-up stats for every date range the formula reads. They go through the same engine as real texts — the formula, the rules, the reuse pool, the prompt groups — so change a metric, a weight or a range and watch them re-rank. They are never sent and never reused; your own texts take their place once they have results.';

interface Props {
  source: ResultsSource;
  onSource: (s: ResultsSource) => void;
  /** The campaign's own texts (none ranked yet); 0 = only the example to show. */
  yours: number;
}

export const ExampleBanner: FC<Props> = ({ source, onSource, yours }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-primary/5 px-3 py-2 text-xs duration-300 animate-in fade-in-0 motion-reduce:animate-none" data-testid="example-banner">
    <span className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-primary">
      <FlaskConical className="h-3 w-3" aria-hidden />
      Example
    </span>
    <span className="text-muted-foreground">{source === 'example' ? 'Made-up texts scored on your formula — never sent' : 'None of your texts is ranked yet'}</span>
    <HintDot label={HINT} />
    {yours > 0 ? (
      <SegmentedPills<ResultsSource>
        className="ml-auto"
        options={[
          { value: 'yours', label: `Your texts (${yours})` },
          { value: 'example', label: 'Example' },
        ]}
        value={source}
        onChange={onSource}
      />
    ) : null}
  </div>
);
